import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ClipboardList, Calendar as CalendarIcon,
  Clock, CheckCircle, XCircle, AlertTriangle,
  User, Edit, Plus, Trash2, Save, ChevronLeft,
  ChevronRight, LayoutList, CalendarDays, RefreshCw
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AttendanceRecords = () => {
  const { selectedBusinessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [staff, setStaff] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [viewMode, setViewMode] = useState("table"); // "table" or "calendar"
  
  // Filters
  const [selectedUser, setSelectedUser] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarUser, setCalendarUser] = useState("");
  
  // Edit/Create attendance modal state
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    punch_in_time: "",
    punch_out_time: "",
    status: "present",
    notes: ""
  });
  const [createForm, setCreateForm] = useState({
    user_id: "",
    date: "",
    punch_in_time: "",
    punch_out_time: "",
    status: "present",
    notes: ""
  });
  const [saving, setSaving] = useState(false);

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i + 1);

  useEffect(() => {
    fetchUserInfo();
    fetchStaff();
  }, [selectedBusinessId]);

  useEffect(() => {
    if (viewMode === "table") {
      fetchRecords();
    }
  }, [selectedUser, startDate, endDate, selectedBusinessId, viewMode]);

  useEffect(() => {
    if (viewMode === "calendar" && calendarUser) {
      fetchCalendarData();
    }
  }, [calendarUser, calendarMonth, calendarYear, selectedBusinessId, viewMode]);

  // Auto-select user for calendar when staff loads (for employees)
  useEffect(() => {
    if (staff.length > 0 && !calendarUser) {
      if (userRole && !["super_admin", "admin", "manager"].includes(userRole)) {
        // Employee - auto-select themselves
        setCalendarUser(currentUserId);
      } else if (staff.length > 0) {
        // Admin - select first staff
        setCalendarUser(staff[0].user_id);
      }
    }
  }, [staff, userRole, currentUserId]);

  const fetchUserInfo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
        setCurrentUserId(data.user_id);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const fetchStaff = async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/staff?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/staff`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setStaff(data);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      let url = `${BACKEND_URL}/api/attendance/records?`;
      
      if (selectedBusinessId) {
        url += `business_id=${selectedBusinessId}&`;
      }
      if (selectedUser && selectedUser !== "all") {
        url += `user_id=${selectedUser}&`;
      }
      if (startDate) {
        url += `start_date=${startDate}&`;
      }
      if (endDate) {
        url += `end_date=${endDate}&`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setRecords(data);
      }
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarData = async () => {
    if (!calendarUser) return;
    
    try {
      setCalendarLoading(true);
      let url = `${BACKEND_URL}/api/attendance/calendar?user_id=${calendarUser}&month=${calendarMonth}&year=${calendarYear}`;
      
      if (selectedBusinessId) {
        url += `&business_id=${selectedBusinessId}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCalendarData(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to load calendar data");
        setCalendarData(null);
      }
    } catch (error) {
      console.error("Error fetching calendar:", error);
      toast.error("Error loading calendar");
      setCalendarData(null);
    } finally {
      setCalendarLoading(false);
    }
  };

  const canViewAll = ["super_admin", "admin", "manager"].includes(userRole);
  const canEdit = ["super_admin", "admin"].includes(userRole);

  // Navigate calendar months
  const prevMonth = () => {
    if (calendarMonth === 1) {
      setCalendarMonth(12);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const nextMonth = () => {
    if (calendarMonth === 12) {
      setCalendarMonth(1);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  const goToToday = () => {
    const now = new Date();
    setCalendarMonth(now.getMonth() + 1);
    setCalendarYear(now.getFullYear());
  };

  // Extract time from ISO string (HH:MM format)
  const extractTime = (isoString) => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      return date.toTimeString().slice(0, 5); // HH:MM
    } catch {
      return "";
    }
  };

  // Open edit dialog
  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setEditForm({
      punch_in_time: extractTime(record.punch_in_time),
      punch_out_time: extractTime(record.punch_out_time),
      status: record.status || "present",
      notes: record.notes || ""
    });
    setEditOpen(true);
  };

  // Save edited record
  const handleSaveEdit = async () => {
    if (!editingRecord) return;
    setSaving(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/attendance/records/${editingRecord.attendance_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            punch_in_time: editForm.punch_in_time || null,
            punch_out_time: editForm.punch_out_time || null,
            status: editForm.status,
            notes: editForm.notes,
            business_id: selectedBusinessId
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Attendance record updated successfully");
        
        if (data.recalculated) {
          const r = data.recalculated;
          if (r.is_late !== undefined) {
            toast.info(`Late status: ${r.is_late ? `Yes (${r.late_minutes} mins)` : 'No'}`);
          }
          if (r.working_hours !== undefined) {
            toast.info(`Working hours: ${r.working_hours.toFixed(1)}h`);
          }
        }
        
        setEditOpen(false);
        setEditingRecord(null);
        fetchRecords();
        if (viewMode === "calendar") {
          fetchCalendarData();
        }
        // Broadcast so dashboards, leave page, salary preview can refetch.
        window.dispatchEvent(new CustomEvent("hrms:data-changed", { detail: { kind: "attendance" } }));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update attendance record");
      }
    } catch (error) {
      console.error("Error updating record:", error);
      toast.error("Failed to update attendance record");
    } finally {
      setSaving(false);
    }
  };

  // Open create dialog
  const handleOpenCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm({
      user_id: calendarUser || "",
      date: today,
      punch_in_time: "",
      punch_out_time: "",
      status: "present",
      notes: ""
    });
    setCreateOpen(true);
  };

  // Create new attendance record
  const handleCreateRecord = async () => {
    if (!createForm.user_id || !createForm.date) {
      toast.error("Please select a user and date");
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/records`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_id: createForm.user_id,
          date: createForm.date,
          punch_in_time: createForm.punch_in_time || null,
          punch_out_time: createForm.punch_out_time || null,
          status: createForm.status,
          notes: createForm.notes,
          business_id: selectedBusinessId
        })
      });
      
      if (response.ok) {
        toast.success("Attendance record created successfully");
        setCreateOpen(false);
        fetchRecords();
        if (viewMode === "calendar") {
          fetchCalendarData();
        }
        window.dispatchEvent(new CustomEvent("hrms:data-changed", { detail: { kind: "attendance" } }));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create attendance record");
      }
    } catch (error) {
      console.error("Error creating record:", error);
      toast.error("Failed to create attendance record");
    } finally {
      setSaving(false);
    }
  };

  // Delete attendance record
  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm("Are you sure you want to delete this attendance record?")) {
      return;
    }
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/attendance/records/${recordId}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      
      if (response.ok) {
        toast.success("Attendance record deleted");
        fetchRecords();
        if (viewMode === "calendar") {
          fetchCalendarData();
        }
        window.dispatchEvent(new CustomEvent("hrms:data-changed", { detail: { kind: "attendance" } }));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete record");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    return new Date(isoString).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const getStatusBadge = (record) => {
    const status = record.status;
    const isLate = record.is_late;
    
    if (status === "present") {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
          isLate 
            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
            : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
        }`}>
          {isLate ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
          {isLate ? "Late" : "Present"}
        </span>
      );
    } else if (status === "half_day") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
          <Clock className="w-3 h-3" />
          Half Day
        </span>
      );
    } else if (status === "leave") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          <CalendarIcon className="w-3 h-3" />
          Leave
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <XCircle className="w-3 h-3" />
          Absent
        </span>
      );
    }
  };

  // Get color classes for calendar cell - Full background fill style
  const getCalendarCellClasses = (day) => {
    const baseClasses = "aspect-square rounded-lg flex flex-col items-center justify-center transition-all cursor-default";
    
    // Color mapping based on status
    const colorMap = {
      present: "bg-emerald-500",
      late: "bg-emerald-500", // Late is still present, just with a label
      absent: "bg-red-500",
      half_day: "bg-yellow-500",
      leave: "bg-blue-500",
      holiday: "bg-slate-400",
      weekly_off: "bg-slate-400",
      future: "bg-slate-200 dark:bg-slate-700"
    };
    
    const bgColor = colorMap[day.status] || colorMap.future;
    
    // Add ring for today
    if (day.is_today) {
      return `${baseClasses} ${bgColor} ring-4 ring-indigo-500 ring-offset-2`;
    }
    
    return `${baseClasses} ${bgColor}`;
  };

  // Check if day should show LATE label
  const shouldShowLateLabel = (day) => {
    return day.status === "late" || (day.status === "present" && day.late_minutes > 0);
  };

  // Get text color for calendar cell
  const getCalendarTextColor = (day) => {
    if (day.status === "future") {
      return "text-slate-400 dark:text-slate-500";
    }
    return "text-white";
  };

  // Build calendar grid
  const buildCalendarGrid = () => {
    if (!calendarData || !calendarData.calendar) return [];
    
    const calendar = calendarData.calendar;
    const firstDay = new Date(calendarYear, calendarMonth - 1, 1).getDay(); // 0 = Sunday
    
    // Create grid with empty cells for days before the 1st
    const grid = [];
    
    // Add empty cells for days before the 1st (Sunday = 0)
    for (let i = 0; i < firstDay; i++) {
      grid.push(null);
    }
    
    // Add all days
    calendar.forEach(day => {
      grid.push(day);
    });
    
    return grid;
  };

  // Set default date range (current month)
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  const calendarGrid = buildCalendarGrid();

  return (
    <div className="p-2 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-6" data-testid="attendance-records-page">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <ClipboardList className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-emerald-600" />
            <span className="hidden sm:inline">Attendance Records</span>
            <span className="sm:hidden">Attendance</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 sm:mt-1">
            View and track attendance history
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { fetchRecords(); if (viewMode === "calendar") fetchCalendarData(); }}
            disabled={loading || calendarLoading}
            className="text-xs sm:text-sm h-8 sm:h-9 md:h-10 px-3 sm:px-4"
            data-testid="refresh-attendance-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${(loading || calendarLoading) ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          {canEdit && (
            <Button
              onClick={handleOpenCreate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm h-8 sm:h-9 md:h-10 px-3 sm:px-4"
              data-testid="create-attendance-btn"
            >
              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Add Attendance</span>
              <span className="sm:hidden">Add</span>
            </Button>
          )}
        </div>
      </div>

      {/* View Toggle - Responsive */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
        <TabsList className="grid w-full max-w-xs sm:max-w-sm md:max-w-md grid-cols-2 h-9 sm:h-10">
          <TabsTrigger value="table" className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm" data-testid="table-view-tab">
            <LayoutList className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Table View</span>
            <span className="sm:hidden">Table</span>
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm" data-testid="calendar-view-tab">
            <CalendarDays className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Calendar View</span>
            <span className="sm:hidden">Calendar</span>
          </TabsTrigger>
        </TabsList>

        {/* Table View */}
        <TabsContent value="table" className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
          {/* Table Filters */}
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {canViewAll && (
                  <div className="w-full sm:flex-1">
                    <Label className="text-xs text-slate-500 mb-1 block">Staff Member</Label>
                    <Select value={selectedUser} onValueChange={setSelectedUser}>
                      <SelectTrigger data-testid="user-filter">
                        <SelectValue placeholder="All Staff" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Staff</SelectItem>
                        {staff.map(s => (
                          <SelectItem key={s.user_id} value={s.user_id}>
                            {s.name || s.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="flex gap-2 sm:gap-4 w-full sm:flex-1">
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 mb-1 block">Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      data-testid="start-date-filter"
                      className="text-sm"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 mb-1 block">End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      data-testid="end-date-filter"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Records Table */}
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-emerald-600"></div>
                </div>
              ) : records.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-slate-500">
                  <ClipboardList className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">No attendance records found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead className="font-semibold text-xs sm:text-sm">Date</TableHead>
                        {canViewAll && <TableHead className="font-semibold text-xs sm:text-sm hidden sm:table-cell">Staff</TableHead>}
                        <TableHead className="font-semibold text-xs sm:text-sm">Status</TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm">In</TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm">Out</TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm hidden md:table-cell">Hours</TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm hidden lg:table-cell">OT</TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm hidden md:table-cell">Late</TableHead>
                        {canEdit && <TableHead className="font-semibold text-xs sm:text-sm">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {records.map((record) => (
                        <TableRow 
                          key={record.attendance_id}
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                          data-testid={`record-row-${record.attendance_id}`}
                        >
                          <TableCell className="font-medium text-xs sm:text-sm py-2 sm:py-3">
                            <span className="hidden sm:inline">{formatDate(record.date)}</span>
                            <span className="sm:hidden">{new Date(record.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                          </TableCell>
                          {canViewAll && (
                            <TableCell className="hidden sm:table-cell py-2 sm:py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                  <User className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-emerald-600" />
                                </div>
                                <span className="text-xs sm:text-sm truncate max-w-[100px]">{record.user_name}</span>
                              </div>
                            </TableCell>
                          )}
                          <TableCell className="py-2 sm:py-3">
                            {getStatusBadge(record)}
                          </TableCell>
                          <TableCell className="text-emerald-600 font-medium text-xs sm:text-sm py-2 sm:py-3">
                            {formatTime(record.punch_in_time)}
                          </TableCell>
                          <TableCell className="text-red-600 font-medium text-xs sm:text-sm py-2 sm:py-3">
                            {formatTime(record.punch_out_time)}
                          </TableCell>
                          <TableCell className="hidden md:table-cell py-2 sm:py-3">
                            <span className="font-medium text-xs sm:text-sm">
                              {record.working_hours?.toFixed(1) || "0"}h
                            </span>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell py-2 sm:py-3">
                            {record.overtime_hours > 0 ? (
                              <span className="text-blue-600 font-medium text-xs sm:text-sm">
                                +{record.overtime_hours.toFixed(1)}h
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs sm:text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden md:table-cell py-2 sm:py-3">
                            {record.is_late ? (
                              <span className="text-amber-600 font-medium text-xs sm:text-sm">
                                {record.late_minutes}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs sm:text-sm">-</span>
                            )}
                          </TableCell>
                          {canEdit && (
                            <TableCell className="py-2 sm:py-3">
                              <div className="flex items-center gap-0.5 sm:gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditRecord(record)}
                                  className="h-6 w-6 sm:h-7 sm:w-7 p-0 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                  data-testid={`edit-record-${record.attendance_id}`}
                                >
                                  <Edit className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-600" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteRecord(record.attendance_id)}
                                  className="h-6 w-6 sm:h-7 sm:w-7 p-0 hover:bg-red-100 dark:hover:bg-red-900/30"
                                  data-testid={`delete-record-${record.attendance_id}`}
                                >
                                  <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-red-600" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary - Responsive grid */}
          {records.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
              <Card className="border-slate-200 dark:border-slate-700 p-2 sm:p-3 md:p-4">
                <div className="text-center">
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-white">
                    {records.filter(r => r.status === "present").length}
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Present</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-2 sm:p-3 md:p-4">
                <div className="text-center">
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-red-600">
                    {records.filter(r => r.status === "absent").length}
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Absent</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-2 sm:p-3 md:p-4">
                <div className="text-center">
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-amber-600">
                    {records.filter(r => r.is_late).length}
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Late</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-2 sm:p-3 md:p-4 hidden md:block">
                <div className="text-center">
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600">
                    {records.reduce((sum, r) => sum + (r.overtime_hours || 0), 0).toFixed(1)}h
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Overtime</p>
                </div>
              </Card>
              <Card className="border-slate-200 dark:border-slate-700 p-2 sm:p-3 md:p-4 hidden md:block">
                <div className="text-center">
                  <p className="text-lg sm:text-xl md:text-2xl font-bold text-emerald-600">
                    {records.reduce((sum, r) => sum + (r.working_hours || 0), 0).toFixed(1)}h
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Hours</p>
                </div>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Calendar View */}
        <TabsContent value="calendar" className="mt-4 space-y-3 sm:space-y-4">
          {/* Calendar Filters - Mobile Optimized */}
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="p-3 sm:p-4">
              {/* Mobile: Stack filters, Desktop: Row layout */}
              <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-row sm:gap-3 sm:items-end">
                {/* Staff Selector - Full width on mobile */}
                <div className="w-full sm:flex-1">
                  <Label className="text-xs text-slate-500 mb-1 block">Staff Member</Label>
                  <Select 
                    value={calendarUser} 
                    onValueChange={setCalendarUser}
                    disabled={!canViewAll}
                  >
                    <SelectTrigger data-testid="calendar-user-select" className="w-full">
                      <SelectValue placeholder="Select staff member" />
                    </SelectTrigger>
                    <SelectContent>
                      {staff.map(s => (
                        <SelectItem key={s.user_id} value={s.user_id}>
                          {s.name || s.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Month & Year Row on Mobile */}
                <div className="flex gap-2 w-full sm:w-auto sm:flex-1">
                  {/* Month Selector */}
                  <div className="flex-1 sm:w-full">
                    <Label className="text-xs text-slate-500 mb-1 block">Month</Label>
                    <Select 
                      value={calendarMonth.toString()} 
                      onValueChange={(v) => setCalendarMonth(parseInt(v))}
                    >
                      <SelectTrigger data-testid="calendar-month-select" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map(m => (
                          <SelectItem key={m.value} value={m.value.toString()}>
                            <span className="hidden sm:inline">{m.label}</span>
                            <span className="sm:hidden">{m.label.slice(0, 3)}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Year Selector */}
                  <div className="flex-1 sm:w-full">
                    <Label className="text-xs text-slate-500 mb-1 block">Year</Label>
                    <Select 
                      value={calendarYear.toString()} 
                      onValueChange={(v) => setCalendarYear(parseInt(v))}
                    >
                      <SelectTrigger data-testid="calendar-year-select" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map(y => (
                          <SelectItem key={y} value={y.toString()}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Navigation Buttons - Centered on mobile */}
                <div className="flex items-center justify-center gap-2 pt-1 sm:pt-0">
                  <Button variant="outline" size="icon" onClick={prevMonth} data-testid="prev-month-btn" className="h-9 w-9 sm:h-10 sm:w-10">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={goToToday} data-testid="today-btn" className="h-9 px-3 sm:h-10 sm:px-4">
                    Today
                  </Button>
                  <Button variant="outline" size="icon" onClick={nextMonth} data-testid="next-month-btn" className="h-9 w-9 sm:h-10 sm:w-10">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Calendar Grid Card */}
          <Card className="border-slate-200 dark:border-slate-700 overflow-hidden">
            <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-4">
              <CardTitle className="flex items-center justify-between text-sm sm:text-base">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                  <span className="truncate">
                    <span className="hidden sm:inline">{calendarData?.user_name || "Select Staff"} - </span>
                    <span className="sm:hidden">{calendarData?.user_name?.split(' ')[0] || "Staff"} - </span>
                    {months.find(m => m.value === calendarMonth)?.label.slice(0, 3)} {calendarYear}
                  </span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 sm:px-4 md:px-6 pb-3 sm:pb-4">
              {calendarLoading ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-emerald-600"></div>
                </div>
              ) : !calendarData ? (
                <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-slate-500">
                  <CalendarDays className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">Select a staff member to view calendar</p>
                </div>
              ) : (
                <>
                  {/* Legend - Compact on mobile, expanded on desktop */}
                  <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4 md:mb-6 p-2 sm:p-3 md:p-4 bg-slate-50 dark:bg-slate-800 rounded-lg sm:rounded-xl">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded bg-emerald-500"></span>
                      <span className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-300">Present</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded bg-red-500"></span>
                      <span className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-300">Absent</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded bg-emerald-500 flex items-center justify-center">
                        <span className="text-[4px] sm:text-[5px] md:text-[6px] text-white font-bold">LATE</span>
                      </span>
                      <span className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-300">Late</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded bg-yellow-500"></span>
                      <span className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-300">Half Day</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded bg-blue-500"></span>
                      <span className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-300">Leave</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded bg-slate-400"></span>
                      <span className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-300">Off</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded bg-slate-200 dark:bg-slate-700"></span>
                      <span className="text-[10px] sm:text-xs md:text-sm text-slate-600 dark:text-slate-300">Future</span>
                    </div>
                  </div>

                  {/* Calendar Grid - Responsive gaps and sizing */}
                  <div className="grid grid-cols-7 gap-1 sm:gap-1.5 md:gap-2 lg:gap-3">
                    {/* Day Headers - Short on mobile */}
                    {[
                      { full: "Sun", short: "S" },
                      { full: "Mon", short: "M" },
                      { full: "Tue", short: "T" },
                      { full: "Wed", short: "W" },
                      { full: "Thu", short: "T" },
                      { full: "Fri", short: "F" },
                      { full: "Sat", short: "S" }
                    ].map((day, i) => (
                      <div 
                        key={i} 
                        className={`text-center py-1 sm:py-2 text-[10px] sm:text-xs md:text-sm lg:text-base font-semibold ${
                          i === 0 ? "text-slate-500" : "text-slate-600 dark:text-slate-300"
                        }`}
                      >
                        <span className="hidden sm:inline">{day.full}</span>
                        <span className="sm:hidden">{day.short}</span>
                      </div>
                    ))}
                    
                    {/* Calendar Cells - Responsive sizing */}
                    {calendarGrid.map((day, idx) => (
                      <div 
                        key={idx} 
                        className={day ? getCalendarCellClasses(day) : "aspect-square"}
                        data-testid={day ? `calendar-day-${day.day}` : undefined}
                      >
                        {day && (
                          <>
                            {/* Day Number - Responsive text size */}
                            <span className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold ${getCalendarTextColor(day)}`}>
                              {String(day.day).padStart(2, '0')}
                            </span>
                            
                            {/* LATE Label - Responsive */}
                            {shouldShowLateLabel(day) && (
                              <span className="text-[6px] sm:text-[8px] md:text-[10px] lg:text-xs font-bold text-white/90 mt-0 sm:mt-0.5 leading-none">
                                LATE
                              </span>
                            )}
                            
                            {/* Half Day Label */}
                            {day.status === "half_day" && (
                              <span className="text-[6px] sm:text-[8px] md:text-[10px] lg:text-xs font-bold text-white/90 mt-0 sm:mt-0.5 leading-none">
                                HD
                              </span>
                            )}
                            
                            {/* Leave Label */}
                            {day.status === "leave" && (
                              <span className="text-[6px] sm:text-[8px] md:text-[10px] lg:text-xs font-bold text-white/90 mt-0 sm:mt-0.5 leading-none">
                                LEAVE
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Summary Cards - Responsive grid */}
                  {calendarData.summary && (
                    <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-7 gap-1.5 sm:gap-2 mt-3 sm:mt-4 md:mt-6">
                      <div className="p-1.5 sm:p-2 md:p-3 bg-green-50 dark:bg-green-900/20 rounded-md sm:rounded-lg text-center">
                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-green-600">{calendarData.summary.present}</p>
                        <p className="text-[8px] sm:text-[10px] md:text-xs text-slate-500 truncate">Present</p>
                      </div>
                      <div className="p-1.5 sm:p-2 md:p-3 bg-red-50 dark:bg-red-900/20 rounded-md sm:rounded-lg text-center">
                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-red-600">{calendarData.summary.absent}</p>
                        <p className="text-[8px] sm:text-[10px] md:text-xs text-slate-500 truncate">Absent</p>
                      </div>
                      <div className="p-1.5 sm:p-2 md:p-3 bg-orange-50 dark:bg-orange-900/20 rounded-md sm:rounded-lg text-center">
                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-orange-600">{calendarData.summary.late}</p>
                        <p className="text-[8px] sm:text-[10px] md:text-xs text-slate-500 truncate">Late</p>
                      </div>
                      <div className="p-1.5 sm:p-2 md:p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md sm:rounded-lg text-center">
                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-yellow-600">{calendarData.summary.half_day}</p>
                        <p className="text-[8px] sm:text-[10px] md:text-xs text-slate-500 truncate">Half Day</p>
                      </div>
                      <div className="p-1.5 sm:p-2 md:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md sm:rounded-lg text-center">
                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-blue-600">{calendarData.summary.leave}</p>
                        <p className="text-[8px] sm:text-[10px] md:text-xs text-slate-500 truncate">Leave</p>
                      </div>
                      <div className="p-1.5 sm:p-2 md:p-3 bg-slate-50 dark:bg-slate-800 rounded-md sm:rounded-lg text-center">
                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-slate-600 dark:text-slate-300">{calendarData.summary.holiday}</p>
                        <p className="text-[8px] sm:text-[10px] md:text-xs text-slate-500 truncate">Holiday</p>
                      </div>
                      <div className="p-1.5 sm:p-2 md:p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-md sm:rounded-lg text-center">
                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-emerald-600">{calendarData.summary.working_days}</p>
                        <p className="text-[8px] sm:text-[10px] md:text-xs text-slate-500 truncate">Work Days</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Attendance Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-attendance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Attendance Record
            </DialogTitle>
          </DialogHeader>
          
          {editingRecord && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-sm font-medium">{editingRecord.user_name}</p>
                <p className="text-xs text-slate-500">{formatDate(editingRecord.date)}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-punch-in" className="text-xs text-slate-500">Punch In Time</Label>
                  <Input
                    id="edit-punch-in"
                    type="time"
                    value={editForm.punch_in_time}
                    onChange={(e) => setEditForm({ ...editForm, punch_in_time: e.target.value })}
                    data-testid="edit-punch-in"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-punch-out" className="text-xs text-slate-500">Punch Out Time</Label>
                  <Input
                    id="edit-punch-out"
                    type="time"
                    value={editForm.punch_out_time}
                    onChange={(e) => setEditForm({ ...editForm, punch_out_time: e.target.value })}
                    data-testid="edit-punch-out"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit-status" className="text-xs text-slate-500">Status</Label>
                <Select 
                  value={editForm.status} 
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger data-testid="edit-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="half_day">Half Day</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="leave">Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit-notes" className="text-xs text-slate-500">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Add notes about this attendance record..."
                  rows={2}
                  data-testid="edit-notes"
                />
              </div>
              
              <p className="text-xs text-slate-500 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                Note: Late mark and working hours will be automatically recalculated based on office start time and grace period settings.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-edit-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Save Changes
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Attendance Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-attendance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-600" />
              Add Attendance Record
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="create-user" className="text-xs text-slate-500">Staff Member *</Label>
              <Select 
                value={createForm.user_id} 
                onValueChange={(value) => setCreateForm({ ...createForm, user_id: value })}
              >
                <SelectTrigger data-testid="create-user">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map(s => (
                    <SelectItem key={s.user_id} value={s.user_id}>
                      {s.name || s.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="create-date" className="text-xs text-slate-500">Date *</Label>
              <Input
                id="create-date"
                type="date"
                value={createForm.date}
                onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })}
                data-testid="create-date"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="create-punch-in" className="text-xs text-slate-500">Punch In Time</Label>
                <Input
                  id="create-punch-in"
                  type="time"
                  value={createForm.punch_in_time}
                  onChange={(e) => setCreateForm({ ...createForm, punch_in_time: e.target.value })}
                  data-testid="create-punch-in"
                />
              </div>
              <div>
                <Label htmlFor="create-punch-out" className="text-xs text-slate-500">Punch Out Time</Label>
                <Input
                  id="create-punch-out"
                  type="time"
                  value={createForm.punch_out_time}
                  onChange={(e) => setCreateForm({ ...createForm, punch_out_time: e.target.value })}
                  data-testid="create-punch-out"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="create-status" className="text-xs text-slate-500">Status</Label>
              <Select 
                value={createForm.status} 
                onValueChange={(value) => setCreateForm({ ...createForm, status: value })}
              >
                <SelectTrigger data-testid="create-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="leave">Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="create-notes" className="text-xs text-slate-500">Notes</Label>
              <Textarea
                id="create-notes"
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                placeholder="Add notes..."
                rows={2}
                data-testid="create-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRecord} 
              disabled={saving || !createForm.user_id || !createForm.date}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-create-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Create Record
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceRecords;
